import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'zh-TW' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  'zh-TW': {
    // Navigation
    'nav.home': '首頁',
    'nav.products': '產品',
    'nav.about': '關於我們',
    'nav.contact': '聯絡我們',
    'nav.cart': '購物車',
    'nav.account': '我的帳戶',
    'nav.admin': '管理後台',
    'nav.login': '登入',
    'nav.logout': '登出',
    
    // Home Page
    'home.hero.title': 'ARVIX',
    'home.hero.kicker': '知識的力量',
    'home.hero.subtitle': '專注於技術教育與知識分享的平台——提供精選電子書、技術指南與學習資源，助您掌握最新科技趨勢。',
    'home.hero.cta': '瀏覽電子書',
    'home.hero.ctaSecondary': '查看所有書籍',
    'home.hero.shipping': '國內配送',
    'home.hero.shippingDays': '2-3 天',
    'home.hero.returns': '輕鬆退貨',
    'home.hero.returnsDays': '30 天',
    'home.hero.secure': '安全',
    'home.hero.secureText': '結帳與隱私',
    'home.featured.title': '精選商品',
    'home.featured.subtitle': '為您的日常精選的必需品',
    'home.collections.title': '探索系列',
    'home.collections.subtitle': '為您日常生活各方面精心策劃的工具',
    'home.collections.viewAll': '選購產品',
    'home.categories.title': '所有類別',
    'home.categories.subtitle': '找到您需要的一切',
    'home.categories.shopNow': '立即選購 →',
    'home.brands.featured': '精選於',
    'home.value.title': '優質學習內容',
    'home.value.subtitle': '精心策劃的電子書、實用指南和全面資源——專為有效學習而設計。',
    'home.value.material': '專家作者',
    'home.value.materialDesc': '由具有實戰經驗和深厚技術知識的業界專業人士創作的內容。',
    'home.value.carry': '即時存取',
    'home.value.carryDesc': '在任何設備上下載和閱讀——隨時隨地按照自己的節奏學習。',
    'home.value.lasting': '持續更新',
    'home.value.lastingDesc': '定期更新內容，確保與最新技術趨勢和最佳實踐保持同步。',
    'home.testimonials.title': '客戶評價',
    'home.testimonials.subtitle': '一些重視平靜、功能和工藝的人的評論。',
    'home.testimonials.quote1': '表面處理令人難以置信。這是您想放在桌上的那種工具。',
    'home.testimonials.quote2': '極簡但不無聊。一切都經過深思熟慮。',
    'home.testimonials.quote3': '完美融入我的日常攜帶。質量超出預期。',
    
    // Product
    'product.addToCart': '加入購物車',
    'product.outOfStock': '缺貨中',
    'product.comingSoon': '即將推出',
    'product.price': '價格',
    'product.description': '描述',
    'product.quantity': '數量',
    'product.viewDetails': '查看詳情',
    'product.view': '查看',
    'product.featured': '精選',
    'product.addedToCart': '已加入購物車',
    'product.continue': '繼續',
    'product.viewCart': '查看購物車',
    'product.notFound': '找不到產品',
    'product.notFoundDesc': '您要找的產品不存在。',
    'product.backToProducts': '返回產品',
    'product.youMightLike': '您可能也喜歡',
    'product.noRelated': '暫無相關產品。',
    'product.specs': '產品規格',
    'product.features': '主要特點',
    'product.feature.premium': '優質材料',
    'product.feature.minimal': '極簡設計',
    'product.feature.durable': '耐用結構',
    'product.feature.easy': '易於使用',
    'product.returns': '30天退貨',
    'product.shipping': '2-3天國內配送',
    
    // Cart
    'cart.title': '購物車',
    'cart.empty': '購物車是空的',
    'cart.continueShopping': '繼續購物',
    'cart.checkout': '結帳',
    'cart.subtotal': '小計',
    'cart.total': '總計',
    'cart.remove': '移除',
    'cart.items': '件商品',
    'cart.estimatedTax': '預估稅金',
    'cart.freeShipping': '免運費',
    'cart.freeShippingHint': '再加',
    'cart.freeShippingMore': '即可享免運費。',
    'cart.recommendations': '您可能也喜歡',
    'cart.checkoutNote': '結帳時計算稅金和運費。您的支付詳情已加密。',
    
    // Checkout
    'checkout.title': '結帳',
    'checkout.orderSummary': '訂單摘要',
    'checkout.paymentMethod': '支付方式',
    'checkout.selectPayment': '選擇支付方式',
    'checkout.proceedToPayment': '前往支付',
    'checkout.processing': '處理中...',
    'checkout.backToCart': '返回購物車',
    'checkout.shipping': '運費',
    'checkout.toBeCalculated': '待計算',
    'checkout.amountDetails': '金額明細',
    'checkout.confirmationNote': '支付完成後，您將收到訂單確認郵件',
    'checkout.step.information': '填寫資料',
    'checkout.step.shipping': '收貨地址',
    'checkout.step.review': '確認訂單',
    'checkout.step.payment': '支付',
    'checkout.cartEmpty': '購物車是空的',
    'checkout.cartEmptyDesc': '請先將商品加入購物車再結帳。',
    'checkout.continueShopping': '繼續購物',
    'checkout.contactInfo': '聯絡資訊',
    'checkout.loggedInAs': '已登入為',
    'checkout.shippingAddress': '收貨地址',
    'checkout.usingDefaultAddress': '使用預設地址',
    'checkout.firstName': '名字 *',
    'checkout.lastName': '姓氏 *',
    'checkout.country': '國家/地區 *',
    'checkout.city': '城市 *',
    'checkout.address': '地址 *',
    'checkout.addressPlaceholder': '街道地址',
    'checkout.address2': '公寓、套房等',
    'checkout.postalCode': '郵遞區號 *',
    'checkout.reviewOrder': '確認訂單',
    'checkout.contact': '聯絡方式',
    'checkout.shipTo': '收貨地址',
    'checkout.paymentStep': '支付',
    'checkout.orderPlaced': '訂單已建立',
    'checkout.orderPlacedDesc': '您的訂單已建立。此為演示，支付功能已停用，無法完成交易。',
    'checkout.order': '訂單',
    'checkout.total': '總計',
    'checkout.payNow': '立即支付',
    'checkout.returnHome': '返回首頁',
    'checkout.back': '上一步',
    'checkout.next': '下一步',
    'checkout.placeOrder': '提交訂單',
    
    // Payment
    'payment.creditCard': '信用卡',
    'payment.atm': 'ATM 轉賬',
    'payment.cvs': '超商代碼',
    'payment.barcode': '超商條碼',
    'payment.newebpay': '藍新金流 (NewebPay)',
    'payment.ecpay': '綠界科技 (ECPay)',
    'payment.creditCard.desc': '支持 Visa、Mastercard、JCB',
    'payment.atm.desc': '虛擬帳號轉賬，3天內完成',
    'payment.cvs.desc': '7-11、全家、萊爾富',
    'payment.barcode.desc': '超商掃碼支付',
    
    // Account
    'account.title': '我的帳戶',
    'account.orders': '我的訂單',
    'account.profile': '個人資料',
    'account.addresses': '收貨地址',
    'account.orderHistory': '訂單記錄',
    'account.orderNumber': '訂單編號',
    'account.date': '日期',
    'account.status': '狀態',
    'account.amount': '金額',
    'account.viewDetails': '查看詳情',
    
    // Order Status
    'order.status.pending': '待處理',
    'order.status.paid': '已付款',
    'order.status.processing': '處理中',
    'order.status.shipped': '已出貨',
    'order.status.delivered': '已送達',
    'order.status.cancelled': '已取消',
    'order.status.refunded': '已退款',
    
    // Auth
    'auth.login': '登入',
    'auth.register': '註冊',
    'auth.email': '電子郵件',
    'auth.password': '密碼',
    'auth.confirmPassword': '確認密碼',
    'auth.forgotPassword': '忘記密碼？',
    'auth.noAccount': '還沒有帳戶？',
    'auth.hasAccount': '已有帳戶？',
    'auth.signUp': '註冊',
    'auth.signIn': '登入',
    
    // Common
    'common.loading': '載入中...',
    'common.error': '錯誤',
    'common.success': '成功',
    'common.cancel': '取消',
    'common.confirm': '確認',
    'common.save': '儲存',
    'common.edit': '編輯',
    'common.delete': '刪除',
    'common.search': '搜尋',
    'common.filter': '篩選',
    'common.sort': '排序',
    'common.viewMore': '查看更多',
    'common.backToTop': '回到頂部',
    
    // Footer
    'footer.company': '公司資訊',
    'footer.about': '關於我們',
    'footer.careers': '人才招募',
    'footer.press': '媒體中心',
    'footer.support': '客戶支持',
    'footer.help': '幫助中心',
    'footer.shipping': '配送資訊',
    'footer.returns': '退換貨政策',
    'footer.contact': '聯絡我們',
    'footer.social': '關注我們',
    'footer.newsletter': '訂閱電子報',
    'footer.newsletterDesc': '訂閱以獲取最新優惠和產品資訊',
    'footer.subscribe': '訂閱',
    'footer.copyright': '© 2024 時光科技. 版權所有.',
    
    // Login/Register
    'login.title': '登入',
    'login.subtitle': '登入您的帳戶',
    'login.submit': '登入',
    'login.noAccount': '還沒有帳戶？',
    'login.createAccount': '建立帳戶',
    'register.title': '註冊',
    'register.subtitle': '建立新帳戶',
    'register.submit': '註冊',
    'register.hasAccount': '已有帳戶？',
    'register.signIn': '登入',
    
    // Account Page
    'account.welcome': '歡迎回來',
    'account.recentOrders': '最近訂單',
    'account.noOrders': '您還沒有任何訂單',
    'account.startShopping': '開始購物',
    'account.viewAll': '查看全部',
    'account.orderDetails': '訂單詳情',
    'account.payNow': '立即支付',
    
    // Product Collection
    'collection.home': '首頁',
    'collection.allProducts': '所有產品',
    'collection.products': '產品',
    'collection.product': '產品',
    'collection.results': '結果',
    'collection.searchPlaceholder': '搜尋產品...',
    'collection.sortBy': '排序：',
    'collection.featured': '精選',
    'collection.priceLowHigh': '價格：低到高',
    'collection.priceHighLow': '價格：高到低',
    'collection.nameAZ': '名稱：A 到 Z',
    'collection.noProducts': '找不到產品',
    'collection.noProductsDesc': '稍後查看此類別的新品。',
    'collection.noSearchResults': '沒有產品匹配',
    'collection.tryDifferent': '嘗試不同的搜尋詞。',
    'collection.shopOther': '選購其他類別',
    'collection.searchTips': '搜尋提示：',
    'collection.checkSpelling': '檢查您的拼寫',
    'collection.tryGeneral': '嘗試更通用的關鍵字',
    'collection.browseCategories': '按類別瀏覽',
    
    // Search
    'search.placeholder': '搜尋產品...',
    'search.searching': '搜尋中...',
    'search.popular': '熱門搜尋',
    'search.resultsFound': '找到結果',
    'search.noResults': '找不到結果',
    'search.tryDifferent': '嘗試使用不同的關鍵字搜尋',
    
    // About & Contact
    'about.title': '關於我們',
    'about.subtitle': '透過優質科技教育和精選數位資源賦能學習者',
    'about.story': '我們的故事',
    'about.storyText1': 'ARVIX 的創立使命是讓科技教育民主化。我們相信優質的學習資源應該讓每個人都能獲得，無論其背景或地點。',
    'about.storyText2': '我們策劃和創作全面的電子書、技術指南和學習材料，幫助開發者、學生和科技愛好者掌握最新技術。從基礎概念到進階主題，我們的內容由了解實際應用的專家設計。',
    'about.philosophy': '我們的理念',
    'about.philosophy.purpose': '教育優先',
    'about.philosophy.purposeDesc': '每個資源都有明確的學習目標和實際應用',
    'about.philosophy.minimal': '優質內容',
    'about.philosophy.minimalDesc': '專業撰寫、徹底審查和定期更新的材料',
    'about.philosophy.craft': '專家作者',
    'about.philosophy.craftDesc': '由具有實戰經驗的業界專業人士創作內容',
    'about.philosophy.innovation': '持續學習',
    'about.philosophy.innovationDesc': '與最新技術趨勢和最佳實踐保持同步',
    'about.journey': '我們的旅程',
    'about.journey.2020': '成立',
    'about.journey.2020desc': 'ARVIX 誕生於對科技教育和知識分享的熱情',
    'about.journey.2021': '首批電子書出版',
    'about.journey.2021desc': '推出我們的旗艦技術指南和教程',
    'about.journey.2022': '社群成長',
    'about.journey.2022desc': '覆蓋全球 25+ 個國家的學習者',
    'about.journey.2024': '擴展圖書館',
    'about.journey.2024desc': '不斷增長的電子書收藏，涵蓋多元科技主題',
    'about.commitment': '我們的承諾',
    'about.commitment.quality': '品質保證',
    'about.commitment.qualityDesc': '每本電子書都經過徹底審查和測試，確保準確性和清晰度',
    'about.commitment.sustainable': '易於學習',
    'about.commitment.sustainableDesc': '實惠的價格和數位格式，隨時隨地輕鬆存取',
    'about.commitment.customer': '學習者成功',
    'about.commitment.customerDesc': '您的學習旅程推動我們的內容創作和改進',
    'about.behind': '我們的使命',
    'about.getInTouch': '聯繫我們',
    'about.support': '客戶支持',
    'about.supportEmail': 'ern@xyvnai.com',
    'about.supportHours': '週一至週五：上午 9:00 - 下午 6:00 (GMT+8)',
    'about.partnerships': '商業合作',
    'about.partnershipsEmail': 'Instagram',
    'about.partnershipsLink': 'https://www.instagram.com/arvix3114?igsh=eXQ0aDYwaTk3OHVx&utm_source=qr',
    'about.stats.products': '電子書',
    'about.stats.customers': '滿意學習者',
    'about.stats.countries': '國家',
    'about.stats.satisfaction': '滿意度',
    'contact.title': '聯絡我們',
    'contact.subtitle': '我們很樂意聽取您的意見。給我們發送消息，我們會盡快回复。',
    'contact.name': '姓名',
    'contact.message': '訊息',
    'contact.send': '發送訊息',
    'contact.success': '訊息已發送！',
    'contact.subject': '主題',
    'contact.subjectPlaceholder': '我們能如何幫助您？',
    'contact.messagePlaceholder': '告訴我們更多關於您的查詢...',
    'contact.info': '聯絡資訊',
    'contact.emailSupport': '電子郵件支持',
    'contact.emailSupportDesc': '我們在工作日 24 小時內回复',
    'contact.socialMedia': '社交媒體',
    'contact.office': '辦公地點',
    'contact.officeLocation': '台北，台灣',
    'contact.officeHours': '週一至週五：上午 9:00 - 下午 6:00',
    'contact.faq': '常見問題',
    'contact.faqDesc': '快速找到常見問題的答案',
    'contact.viewFaq': '查看常見問題',
    'contact.expect': '期待什麼',
    'contact.responseTime': '回應時間',
    'contact.responseTimeDesc': '商業查詢 24 小時內',
    'contact.supportHours': '支持時間',
    'contact.supportHoursDesc': '週一至週五，上午 9:00 - 下午 6:00 (GMT+8)',
    'contact.languages': '語言',
    'contact.languagesDesc': '英語、中文（繁體和簡體）',
    'contact.faqIntro': '關於結帳、配送和退貨的快速答案。',
    
    // Footer Categories
    'footer.category.ebooks': '電子書',
    'footer.privacyPolicy': '隱私政策',
    'footer.termsOfService': '服務條款',
    'footer.refundPolicy': '退款政策',
    
    // Mega Menu
    'mega.viewAll': '查看全部',
    'mega.explore': '探索',
    'mega.viewCollection': '查看系列',
    
    // FAQ
    'faq.q1': '為什麼支付按鈕被禁用？',
    'faq.a1': '這個網站是結帳流程的演示。可以創建訂單，但支付功能被故意禁用。',
    'faq.q2': '我需要帳戶才能下訂單嗎？',
    'faq.a2': '不需要。您可以作為訪客結帳。創建帳戶可以幫助您更輕鬆地追蹤訂單。',
    'faq.q3': '你們接受哪些支付方式？',
    'faq.a3': '我們支持主要信用卡（Visa、Mastercard、AMEX）和流行的數位錢包。在此演示中，不會處理任何支付。',
    'faq.q4': '配送需要多長時間？',
    'faq.a4': '國內訂單通常在 2-3 個工作日內送達。國際配送通常需要 7-14 個工作日，具體取決於目的地。',
    'faq.q5': '你們提供國際配送嗎？',
    'faq.a5': '是的。配送費用和交貨時間因國家而異，將在結帳時顯示。',
    'faq.q6': '我可以追蹤我的訂單嗎？',
    'faq.a6': '可以。發貨後，我們會通過電子郵件向您發送追蹤號碼。',
    'faq.q7': '你們的退貨政策是什麼？',
    'faq.a7': '未使用的商品在原包裝中可在 30 天內退貨。請聯繫客服開始退貨流程。',
    'faq.q8': '我的包裹丟失或損壞了。我該怎麼辦？',
    'faq.a8': '請聯繫我們並提供您的訂單號，我們將協助您與承運商解決問題。',
    'faq.stillQuestions': '還有問題嗎？',
    'faq.contactSupport': '給我們發送消息，我們的客服團隊會回覆您。',
    
    // Order Detail
    'orderDetail.title': '訂單詳情',
    'orderDetail.back': '← 返回',
    'orderDetail.orderInfo': '訂單資訊',
    'orderDetail.orderNumber': '訂單號',
    'orderDetail.orderTime': '下單時間',
    'orderDetail.orderStatus': '訂單狀態',
    'orderDetail.paymentStatus': '支付狀態',
    'orderDetail.paid': '已支付',
    'orderDetail.unpaid': '未支付',
    'orderDetail.payNow': '立即支付',
    'orderDetail.deleteOrder': '刪除訂單',
    'orderDetail.deleteConfirmTitle': '刪除訂單',
    'orderDetail.deleteConfirmMessage': '確定要刪除此訂單嗎？此操作無法撤銷。',
    'orderDetail.deleteConfirmButton': '刪除',
    'orderDetail.cancelButton': '取消',
    'orderDetail.deleteSuccess': '訂單已刪除',
    'orderDetail.deleteFailed': '刪除失敗，請稍後重試',
    'orderDetail.productList': '商品清單',
    'orderDetail.quantity': '數量',
    'orderDetail.unitPrice': '單價',
    'orderDetail.noProducts': '暫無商品資訊',
    'orderDetail.shippingInfo': '收貨資訊',
    'orderDetail.recipient': '收貨人',
    'orderDetail.phone': '聯絡電話',
    'orderDetail.email': '郵箱',
    'orderDetail.address': '收貨地址',
    'orderDetail.notProvided': '未提供',
    'orderDetail.amountSummary': '金額匯總',
    'orderDetail.subtotal': '商品小計',
    'orderDetail.shipping': '運費',
    'orderDetail.total': '訂單總額',
    'orderDetail.loading': '載入中...',
    'orderDetail.notFound': '訂單不存在',
    'orderDetail.backToAccount': '返回我的帳戶',
    'orderDetail.cannotLoad': '無法載入訂單詳情',
  },
  'en': {
    // Navigation
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.cart': 'Cart',
    'nav.account': 'Account',
    'nav.admin': 'Admin',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    
    // Home Page
    'home.hero.title': 'ARVIX',
    'home.hero.kicker': 'The Power of Knowledge',
    'home.hero.subtitle': 'A platform focused on tech education and knowledge sharing—offering curated e-books, technical guides, and learning resources to help you master the latest technology trends.',
    'home.hero.cta': 'Browse E-books',
    'home.hero.ctaSecondary': 'View All Books',
    'home.hero.shipping': 'Domestic shipping',
    'home.hero.shippingDays': '2–3 days',
    'home.hero.returns': 'Easy returns',
    'home.hero.returnsDays': '30 days',
    'home.hero.secure': 'Secure',
    'home.hero.secureText': 'Checkout & privacy',
    'home.featured.title': 'Featured Products',
    'home.featured.subtitle': 'Handpicked essentials for your everyday',
    'home.collections.title': 'Explore Collections',
    'home.collections.subtitle': 'Curated tools for every aspect of your daily life',
    'home.collections.viewAll': 'Shop Products',
    'home.categories.title': 'All Categories',
    'home.categories.subtitle': 'Find exactly what you need',
    'home.categories.shopNow': 'Shop Now →',
    'home.brands.featured': 'Featured in',
    'home.value.title': 'Learn with Quality Content',
    'home.value.subtitle': 'Expertly curated e-books, practical guides, and comprehensive resources—designed for effective learning.',
    'home.value.material': 'Expert Authors',
    'home.value.materialDesc': 'Content created by industry professionals with real-world experience and deep technical knowledge.',
    'home.value.carry': 'Instant Access',
    'home.value.carryDesc': 'Download and read on any device—learn anywhere, anytime at your own pace.',
    'home.value.lasting': 'Always Updated',
    'home.value.lastingDesc': 'Regular updates to keep content current with the latest technology trends and best practices.',
    'home.testimonials.title': 'What customers say',
    'home.testimonials.subtitle': 'A few notes from people who value calm, function, and craft.',
    'home.testimonials.quote1': 'The finish is unreal. It\'s the kind of tool you want to keep on your desk.',
    'home.testimonials.quote2': 'Minimal but not boring. Everything feels considered.',
    'home.testimonials.quote3': 'Fits perfectly in my everyday carry. Quality is better than expected.',
    
    // Product
    'product.addToCart': 'Add to Cart',
    'product.outOfStock': 'Out of Stock',
    'product.comingSoon': 'Coming Soon',
    'product.price': 'Price',
    'product.description': 'Description',
    'product.quantity': 'Quantity',
    'product.viewDetails': 'View Details',
    'product.view': 'View',
    'product.featured': 'Featured',
    'product.addedToCart': 'Added to cart',
    'product.continue': 'Continue',
    'product.viewCart': 'View cart',
    'product.notFound': 'Product Not Found',
    'product.notFoundDesc': 'The product you\'re looking for doesn\'t exist.',
    'product.backToProducts': 'Back to Products',
    'product.youMightLike': 'You might also like',
    'product.noRelated': 'No related products yet.',
    'product.specs': 'Product Specs',
    'product.features': 'Key Features',
    'product.feature.premium': 'Premium Materials',
    'product.feature.minimal': 'Minimalist Design',
    'product.feature.durable': 'Durable Construction',
    'product.feature.easy': 'Easy to Use',
    'product.returns': '30-day returns',
    'product.shipping': '2–3 day domestic shipping',
    
    // Cart
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.continueShopping': 'Continue Shopping',
    'cart.checkout': 'Checkout',
    'cart.subtotal': 'Subtotal',
    'cart.total': 'Total',
    'cart.remove': 'Remove',
    'cart.items': 'items',
    'cart.estimatedTax': 'Estimated tax',
    'cart.freeShipping': 'Free',
    'cart.freeShippingHint': 'Add',
    'cart.freeShippingMore': 'more for free shipping.',
    'cart.recommendations': 'You might also like',
    'cart.checkoutNote': 'Taxes and shipping calculated at checkout. Your payment details are encrypted.',
    
    // Checkout
    'checkout.title': 'Checkout',
    'checkout.orderSummary': 'Order Summary',
    'checkout.paymentMethod': 'Payment Method',
    'checkout.selectPayment': 'Select Payment Method',
    'checkout.proceedToPayment': 'Proceed to Payment',
    'checkout.processing': 'Processing...',
    'checkout.backToCart': 'Back to Cart',
    'checkout.shipping': 'Shipping',
    'checkout.toBeCalculated': 'To be calculated',
    'checkout.amountDetails': 'Amount Details',
    'checkout.confirmationNote': 'You will receive an order confirmation email after payment',
    'checkout.step.information': 'Information',
    'checkout.step.shipping': 'Shipping',
    'checkout.step.review': 'Review',
    'checkout.step.payment': 'Payment',
    'checkout.cartEmpty': 'Your cart is empty',
    'checkout.cartEmptyDesc': 'Please add items to your cart before checking out.',
    'checkout.continueShopping': 'Continue shopping',
    'checkout.contactInfo': 'Contact information',
    'checkout.loggedInAs': 'Logged in as',
    'checkout.shippingAddress': 'Shipping address',
    'checkout.usingDefaultAddress': 'Using default address',
    'checkout.firstName': 'First name *',
    'checkout.lastName': 'Last name *',
    'checkout.country': 'Country/Region *',
    'checkout.city': 'City *',
    'checkout.address': 'Address *',
    'checkout.addressPlaceholder': 'Street address',
    'checkout.address2': 'Apartment, suite, etc.',
    'checkout.postalCode': 'Postal code *',
    'checkout.reviewOrder': 'Review your order',
    'checkout.contact': 'Contact',
    'checkout.shipTo': 'Ship to',
    'checkout.paymentStep': 'Payment',
    'checkout.orderPlaced': 'Order placed',
    'checkout.orderPlacedDesc': 'Your order has been created. In this demo, payment is disabled, so you cannot complete the transaction.',
    'checkout.order': 'Order',
    'checkout.total': 'Total',
    'checkout.payNow': 'Pay now',
    'checkout.returnHome': 'Return to home',
    'checkout.back': 'Back',
    'checkout.next': 'Next',
    'checkout.placeOrder': 'Place order',
    
    // Payment
    'payment.creditCard': 'Credit Card',
    'payment.atm': 'ATM Transfer',
    'payment.cvs': 'CVS Code',
    'payment.barcode': 'CVS Barcode',
    'payment.newebpay': 'NewebPay',
    'payment.ecpay': 'ECPay',
    'payment.creditCard.desc': 'Visa, Mastercard, JCB',
    'payment.atm.desc': 'Virtual account, complete within 3 days',
    'payment.cvs.desc': '7-11, FamilyMart, Hi-Life',
    'payment.barcode.desc': 'Scan at convenience store',
    
    // Account
    'account.title': 'My Account',
    'account.orders': 'My Orders',
    'account.profile': 'Profile',
    'account.addresses': 'Addresses',
    'account.orderHistory': 'Order History',
    'account.orderNumber': 'Order Number',
    'account.date': 'Date',
    'account.status': 'Status',
    'account.amount': 'Amount',
    'account.viewDetails': 'View Details',
    
    // Order Status
    'order.status.pending': 'Pending',
    'order.status.paid': 'Paid',
    'order.status.processing': 'Processing',
    'order.status.shipped': 'Shipped',
    'order.status.delivered': 'Delivered',
    'order.status.cancelled': 'Cancelled',
    'order.status.refunded': 'Refunded',
    
    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.signUp': 'Sign Up',
    'auth.signIn': 'Sign In',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.viewMore': 'View More',
    'common.backToTop': 'Back to Top',
    
    // Footer
    'footer.company': 'Company',
    'footer.about': 'About Us',
    'footer.careers': 'Careers',
    'footer.press': 'Press',
    'footer.support': 'Support',
    'footer.help': 'Help Center',
    'footer.shipping': 'Shipping Info',
    'footer.returns': 'Returns Policy',
    'footer.contact': 'Contact Us',
    'footer.social': 'Follow Us',
    'footer.newsletter': 'Newsletter',
    'footer.newsletterDesc': 'Subscribe for latest offers and updates',
    'footer.subscribe': 'Subscribe',
    'footer.copyright': '© 2024 Seedlight Tech. All rights reserved.',
    
    // Login/Register
    'login.title': 'Login',
    'login.subtitle': 'Sign in to your account',
    'login.submit': 'Sign In',
    'login.noAccount': "Don't have an account?",
    'login.createAccount': 'Create Account',
    'register.title': 'Register',
    'register.subtitle': 'Create a new account',
    'register.submit': 'Register',
    'register.hasAccount': 'Already have an account?',
    'register.signIn': 'Sign In',
    
    // Account Page
    'account.welcome': 'Welcome back',
    'account.recentOrders': 'Recent Orders',
    'account.noOrders': 'You have no orders yet',
    'account.startShopping': 'Start Shopping',
    'account.viewAll': 'View All',
    'account.orderDetails': 'Order Details',
    'account.payNow': 'Pay Now',
    
    // Product Pages
    'product.relatedProducts': 'Related Products',
    'product.specifications': 'Specifications',
    'product.reviews': 'Reviews',
    'product.inStock': 'In Stock',
    'product.limitedStock': 'Limited Stock',
    
    // Product Collection
    'collection.home': 'Home',
    'collection.allProducts': 'All Products',
    'collection.products': 'products',
    'collection.product': 'product',
    'collection.results': 'results',
    'collection.searchPlaceholder': 'Search products...',
    'collection.sortBy': 'Sort by:',
    'collection.featured': 'Featured',
    'collection.priceLowHigh': 'Price: Low to High',
    'collection.priceHighLow': 'Price: High to Low',
    'collection.nameAZ': 'Name: A to Z',
    'collection.noProducts': 'No products found',
    'collection.noProductsDesc': 'Check back later for new arrivals in this category.',
    'collection.noSearchResults': 'No products match',
    'collection.tryDifferent': 'Try a different search term.',
    'collection.shopOther': 'Shop Other Categories',
    'collection.searchTips': 'Search tips:',
    'collection.checkSpelling': 'Check your spelling',
    'collection.tryGeneral': 'Try more general keywords',
    'collection.browseCategories': 'Browse by categories',
    
    // Search
    'search.placeholder': 'Search products...',
    'search.searching': 'Searching...',
    'search.popular': 'Popular Searches',
    'search.resultsFound': 'results found',
    'search.noResults': 'No results found',
    'search.tryDifferent': 'Try searching with different keywords',
    
    // About & Contact
    'about.title': 'About Us',
    'about.subtitle': 'Empowering learners through quality tech education and curated digital resources',
    'about.story': 'Our Story',
    'about.storyText1': 'ARVIX was founded with a mission to democratize tech education. We believe that quality learning resources should be accessible to everyone, regardless of their background or location.',
    'about.storyText2': 'We curate and create comprehensive e-books, technical guides, and learning materials that help developers, students, and tech enthusiasts master the latest technologies. From foundational concepts to advanced topics, our content is designed by experts who understand real-world applications.',
    'about.philosophy': 'Our Philosophy',
    'about.philosophy.purpose': 'Education First',
    'about.philosophy.purposeDesc': 'Every resource is created with clear learning objectives and practical applications',
    'about.philosophy.minimal': 'Quality Content',
    'about.philosophy.minimalDesc': 'Expertly written, thoroughly reviewed, and regularly updated materials',
    'about.philosophy.craft': 'Expert Authors',
    'about.philosophy.craftDesc': 'Content created by industry professionals with real-world experience',
    'about.philosophy.innovation': 'Continuous Learning',
    'about.philosophy.innovationDesc': 'Staying current with the latest technology trends and best practices',
    'about.journey': 'Our Journey',
    'about.journey.2020': 'Founded',
    'about.journey.2020desc': 'ARVIX was born from a passion for tech education and knowledge sharing',
    'about.journey.2021': 'First E-books Published',
    'about.journey.2021desc': 'Launched our flagship technical guides and tutorials',
    'about.journey.2022': 'Community Growth',
    'about.journey.2022desc': 'Reached learners in 25+ countries worldwide',
    'about.journey.2024': 'Expanding Library',
    'about.journey.2024desc': 'Growing collection of e-books covering diverse tech topics',
    'about.commitment': 'Our Commitment',
    'about.commitment.quality': 'Quality Assurance',
    'about.commitment.qualityDesc': 'Every e-book is thoroughly reviewed and tested to ensure accuracy and clarity',
    'about.commitment.sustainable': 'Accessible Learning',
    'about.commitment.sustainableDesc': 'Affordable pricing and digital format for easy access anywhere, anytime',
    'about.commitment.customer': 'Learner Success',
    'about.commitment.customerDesc': 'Your learning journey drives our content creation and improvements',
    'about.behind': 'Our Mission',
    'about.getInTouch': 'Get in Touch',
    'about.support': 'Customer Support',
    'about.supportEmail': 'ern@xyvnai.com',
    'about.supportHours': 'Mon-Fri: 9:00 AM - 6:00 PM (GMT+8)',
    'about.partnerships': 'Business Partnerships',
    'about.partnershipsEmail': 'Instagram',
    'about.partnershipsLink': 'https://www.instagram.com/arvix3114?igsh=eXQ0aDYwaTk3OHVx&utm_source=qr',
    'about.stats.products': 'E-books',
    'about.stats.customers': 'Happy Learners',
    'about.stats.countries': 'Countries',
    'about.stats.satisfaction': 'Satisfaction',
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
    'contact.name': 'Name',
    'contact.message': 'Message',
    'contact.send': 'Send Message',
    'contact.success': 'Message sent!',
    'contact.subject': 'Subject',
    'contact.subjectPlaceholder': 'How can we help?',
    'contact.messagePlaceholder': 'Tell us more about your inquiry...',
    'contact.info': 'Contact Information',
    'contact.emailSupport': 'Email Support',
    'contact.emailSupportDesc': 'We respond within 24 hours on weekdays',
    'contact.socialMedia': 'Social Media',
    'contact.office': 'Office Location',
    'contact.officeLocation': 'Taipei, Taiwan',
    'contact.officeHours': 'Mon-Fri: 9:00 AM - 6:00 PM',
    'contact.faq': 'FAQ',
    'contact.faqDesc': 'Find quick answers to common questions',
    'contact.viewFaq': 'View FAQ',
    'contact.expect': 'What to Expect',
    'contact.responseTime': 'Response Time',
    'contact.responseTimeDesc': 'Within 24 hours for business inquiries',
    'contact.supportHours': 'Support Hours',
    'contact.supportHoursDesc': 'Monday - Friday, 9:00 AM - 6:00 PM (GMT+8)',
    'contact.languages': 'Languages',
    'contact.languagesDesc': 'English, Chinese (Traditional & Simplified)',
    'contact.faqIntro': 'Quick answers about checkout, shipping, and returns.',
    
    // Footer Categories
    'footer.category.ebooks': 'E-books',
    'footer.privacyPolicy': 'Privacy policy',
    'footer.termsOfService': 'Terms of service',
    'footer.refundPolicy': 'Refund policy',
    
    // Mega Menu
    'mega.viewAll': 'View all',
    'mega.explore': 'Explore',
    'mega.viewCollection': 'View collection',
    
    // FAQ
    'faq.q1': 'Why is the payment button disabled?',
    'faq.a1': 'This site is a demo of the checkout flow. Orders can be created, but payment is intentionally disabled.',
    'faq.q2': 'Do I need an account to place an order?',
    'faq.a2': 'No. You can checkout as a guest. Creating an account helps you track your orders more easily.',
    'faq.q3': 'What payment methods do you accept?',
    'faq.a3': 'We support major credit cards (Visa, Mastercard, AMEX) and popular digital wallets. In this demo, no payment is processed.',
    'faq.q4': 'How long does shipping take?',
    'faq.a4': 'Domestic orders typically arrive within 2-3 business days. International shipping usually takes 7-14 business days depending on destination.',
    'faq.q5': 'Do you ship internationally?',
    'faq.a5': 'Yes. Shipping costs and delivery times vary by country and will be shown at checkout.',
    'faq.q6': 'Can I track my order?',
    'faq.a6': 'Yes. After shipment, we will send you a tracking number via email.',
    'faq.q7': 'What is your return policy?',
    'faq.a7': 'Returns are accepted within 30 days for unused items in original packaging. Contact support to start a return.',
    'faq.q8': 'My package is lost or damaged. What should I do?',
    'faq.a8': 'Contact us with your order number and we will help resolve it with the carrier.',
    'faq.stillQuestions': 'Still have questions?',
    'faq.contactSupport': 'Send us a message and our support team will get back to you.',
    
    // Order Detail
    'orderDetail.title': 'Order Details',
    'orderDetail.back': '← Back',
    'orderDetail.orderInfo': 'Order Information',
    'orderDetail.orderNumber': 'Order Number',
    'orderDetail.orderTime': 'Order Time',
    'orderDetail.orderStatus': 'Order Status',
    'orderDetail.paymentStatus': 'Payment Status',
    'orderDetail.paid': 'Paid',
    'orderDetail.unpaid': 'Unpaid',
    'orderDetail.payNow': 'Pay Now',
    'orderDetail.deleteOrder': 'Delete Order',
    'orderDetail.deleteConfirmTitle': 'Delete Order',
    'orderDetail.deleteConfirmMessage': 'Are you sure you want to delete this order? This action cannot be undone.',
    'orderDetail.deleteConfirmButton': 'Delete',
    'orderDetail.cancelButton': 'Cancel',
    'orderDetail.deleteSuccess': 'Order deleted',
    'orderDetail.deleteFailed': 'Delete failed, please try again later',
    'orderDetail.productList': 'Product List',
    'orderDetail.quantity': 'Quantity',
    'orderDetail.unitPrice': 'Unit Price',
    'orderDetail.noProducts': 'No product information',
    'orderDetail.shippingInfo': 'Shipping Information',
    'orderDetail.recipient': 'Recipient',
    'orderDetail.phone': 'Phone',
    'orderDetail.email': 'Email',
    'orderDetail.address': 'Address',
    'orderDetail.notProvided': 'Not provided',
    'orderDetail.amountSummary': 'Amount Summary',
    'orderDetail.subtotal': 'Subtotal',
    'orderDetail.shipping': 'Shipping',
    'orderDetail.total': 'Total',
    'orderDetail.loading': 'Loading...',
    'orderDetail.notFound': 'Order not found',
    'orderDetail.backToAccount': 'Back to My Account',
    'orderDetail.cannotLoad': 'Cannot load order details',
  }
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'en' || saved === 'zh-TW') ? saved : 'zh-TW';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
